import { useNavigate } from '@tanstack/react-router';
import { Building2, ChevronDown, Check, LayoutGrid } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useSelectedUnit } from '../hooks/useSelectedUnit';
import type { MyUnit } from '../types';

/**
 * ADR 2026-09-05 D1: the company is the crumb. The ownership unit surfaces
 * only when it adds information — a company running a second industry has a
 * unit named "Company · Industry" (server-derived), and a legacy unit may
 * carry its own name — so the unit crumb appears exactly when its name
 * differs from the company's. A unit with no company shows its own name.
 */
function unitCrumb(u: MyUnit): string {
  const company = u.legalEntity?.name;
  if (!company) return u.name;
  return u.name === company ? company : `${company}  ›  ${u.name}`;
}

/** Secondary line for a unit: its industry, plus the unit name when it differs from the company. */
function unitDetail(u: MyUnit): string {
  const parts = [u.businessLine?.name];
  if (u.legalEntity && u.name !== u.legalEntity.name) parts.push(u.name);
  return parts.filter(Boolean).join(' · ');
}

/**
 * Workspace-entry: the always-visible "where am I" breadcrumb in the CRM header
 * — company, then the unit only when a company runs more than one. Replaces
 * the bare unit switcher: it shows the legal entity you're operating as, stays
 * visible for single-unit users (read-only), and for a multi-unit member or an
 * all-units admin opens a menu to switch companies or jump back to the overview.
 */
export function WorkspaceBreadcrumb() {
  const navigate = useNavigate();
  const { units, selectedUnit, selectUnit, clearSelection, reach, hasValidStoredPick, isLoading } =
    useSelectedUnit();

  if (isLoading || units.length === 0) return null;

  const isAdmin = reach === 'ALL_UNITS';
  // Admin who hasn't drilled into a unit is operating group-wide.
  const atAllUnits = isAdmin && !hasValidStoredPick;
  // A multi-unit member who hasn't picked this login session must not see a
  // unit the app invented for them — the crumb stays neutral until they choose
  // (the shell gate is routing them to the chooser at this very moment).
  const memberUnpicked = !isAdmin && units.length > 1 && !hasValidStoredPick;

  const label = atAllUnits
    ? 'All units · group-wide'
    : memberUnpicked
      ? 'Choose workspace…'
      : selectedUnit
        ? unitCrumb(selectedUnit)
        : 'Select a unit';

  // A single-unit member has nothing to switch — show the crumb read-only so
  // they still always know their company.
  const canOpen = isAdmin || units.length > 1;

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      className="h-9 max-w-[320px] gap-2 font-mono text-xs"
      aria-label="Current workspace"
      disabled={!canOpen}
    >
      {atAllUnits ? (
        <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className="truncate">{label}</span>
      {canOpen && <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
    </Button>
  );

  if (!canOpen) return trigger;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
        {isAdmin && (
          <>
            <DropdownMenuItem
              onClick={() => {
                clearSelection();
                navigate({ to: '/crm/overview' });
              }}
              className="gap-2"
            >
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <div className="font-medium">All units</div>
                <div className="text-xs text-muted-foreground">Group-wide overview</div>
              </div>
              {atAllUnits && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {units.map((u) => {
          const active = !atAllUnits && !memberUnpicked && selectedUnit?.id === u.id;
          return (
            <DropdownMenuItem key={u.id} onClick={() => selectUnit(u.id)} className="gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{u.legalEntity?.name ?? u.name}</div>
                <div className="truncate text-xs text-muted-foreground">{unitDetail(u)}</div>
              </div>
              {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
